import type { ReactNode } from "react";
import type { BlogPost } from "./blog-posts";

const P = ({ children }: { children: ReactNode }) => (
  <p className="my-4 leading-relaxed text-muted-foreground">{children}</p>
);
const H2 = ({ children, id }: { children: ReactNode; id?: string }) => (
  <h2 id={id} className="mt-10 scroll-mt-24 text-2xl font-bold tracking-tight md:text-3xl">
    {children}
  </h2>
);
const H3 = ({ children }: { children: ReactNode }) => (
  <h3 className="mt-6 text-lg font-semibold">{children}</h3>
);
const UL = ({ children }: { children: ReactNode }) => (
  <ul className="my-4 list-disc space-y-2 pl-5 text-muted-foreground">{children}</ul>
);
const Strong = ({ children }: { children: ReactNode }) => (
  <strong className="text-foreground">{children}</strong>
);

export type Locale = "en" | "es";

// English translations
const POSTS_EN: BlogPost[] = [
  {
    slug: "papel-das-redes-sociais-no-atendimento",
    title: "The role of social media in customer service",
    description:
      "How to use social media in customer service: real-time response, transparency, and how Kentro's omnichannel CRM centralizes everything.",
    keyword: "customer service on social media",
    category: "Customer Service",
    author: "Kentro Team",
    date: "2026-05-14",
    readingMinutes: 6,
    related: ["cliente-no-centro-da-empresa", "confirmacao-de-consulta-pelo-whatsapp"],
    internalLinks: [
      { label: "Omnichannel strategy", href: "/en/omnichannel" },
      { label: "Service management", href: "/en/gestao-de-atendimento" },
    ],
    content: (
      <>
        <P>
          Social media is no longer just a brand showcase: today it is the first channel where most
          people look for answers, complaints or to buy. Providing great service on social media
          means meeting customers on their preferred ground.
        </P>
        <H2 id="why-essential">Why social media is essential in customer service</H2>
        <P>
          <Strong>Customer service on social media</Strong> has three game-changing traits: it is
          public, instant and informal. These pressure companies to be faster — and more transparent.
        </P>
        <H2 id="real-time">Real-time service</H2>
        <P>
          A customer who reaches out on Instagram or WhatsApp expects an answer in minutes, not
          hours. Teams that cannot keep up lose sales and become exposed to negative comments.
          Automating the first response with AI and smart routing reduces wait time without losing
          personalization.
        </P>
        <H2 id="transparency">Transparency: service that everyone sees</H2>
        <P>
          Every public interaction becomes a reference. A well-handled comment converts other
          prospects. A poorly handled case becomes a screenshot that circulates. That is why tone
          and posture must be standardized across all channels.
        </P>
        <H2 id="unified">Unified view with an omnichannel platform</H2>
        <P>
          When WhatsApp, Instagram, Facebook, email and voice live in separate systems, the
          customer repeats the story in every channel. An{" "}
          <a className="text-primary-glow hover:underline" href="/en/omnichannel">omnichannel</a>{" "}
          platform brings everything together in a single history with a unified customer identity.
        </P>
        <H2 id="crm-bot">CRM and chatbot to scale service</H2>
        <P>
          Kentro's CRM keeps the full history of every conversation, applies tags and opportunities
          and triggers automatic flows. The AI chatbot handles repetitive questions and frees the
          human team for what truly requires care.
        </P>
        <H2 id="advocates">How to turn customers into brand advocates</H2>
        <UL>
          <li>Reply fast — even just to say "I'm on it".</li>
          <li>Be human: sign with the agent's name, avoid robotic replies.</li>
          <li>Solve publicly, conclude privately.</li>
          <li>Use data to personalize — call by name and reference the order.</li>
        </UL>
        <H2 id="conclusion">Conclusion</H2>
        <P>
          Great service on social media is less about tools and more about process — but the right
          tool multiplies the team's capacity. Centralizing channels, automating the obvious and
          personalizing what matters is how Brazilian brands are turning service into competitive
          advantage.
        </P>
      </>
    ),
  },
  {
    slug: "atualizacao-audio-whatsapp",
    title: "WhatsApp news: everything about the audio update",
    description:
      "Understand WhatsApp's audio update (color change for listened audios) and how omnichannel tools help companies stay current.",
    keyword: "WhatsApp audio update",
    category: "WhatsApp",
    author: "Kentro Team",
    date: "2026-04-22",
    readingMinutes: 5,
    related: ["terceiro-visto-azul-whatsapp", "confirmacao-de-consulta-pelo-whatsapp"],
    internalLinks: [{ label: "Service management", href: "/en/gestao-de-atendimento" }],
    faq: [
      { q: "What changed in WhatsApp audio?", a: "The visual indication of listened audios: the waveform of heard audios appears in a different color from unheard ones." },
      { q: "Does it affect businesses?", a: "Yes. WhatsApp service requires tracking UX changes to guide teams and adjust processes." },
    ],
    content: (
      <>
        <P>
          WhatsApp launched a seemingly small update with a direct impact on service routines:{" "}
          <Strong>audios that have already been listened to now appear in a different color</Strong>{" "}
          from those still unheard. For anyone handling high volumes of messages, this detail
          changes the workflow.
        </P>
        <H2 id="what-changed">Understanding the WhatsApp audio update</H2>
        <P>
          The change is visual: the audio waveform appears in a lighter tone once you have listened
          to it, making it easier to spot what still needs attention. In long conversations, this
          prevents important messages from being missed.
        </P>
        <H2 id="omnichannel">The importance of omnichannel tools</H2>
        <P>
          In a professional operation, listening to every audio inside the app may not be viable.
          On an omnichannel platform like Kentro, audios enter the same service queue with
          automatic transcription, tags and read indicators — regardless of what the WhatsApp app
          shows.
        </P>
        <H2 id="why-track">Why follow WhatsApp updates</H2>
        <UL>
          <li>New features change customer expectations within weeks.</li>
          <li>Teams need to be trained to use (or not) what the app offers.</li>
          <li>API users need to know what changes in the app vs. the protocol.</li>
        </UL>
        <H2 id="conclusion">Conclusion</H2>
        <P>
          The audio update is a reminder: WhatsApp's UX evolves constantly. Those who combine the
          app with a service platform gain predictability — and are not held hostage to small
          changes.
        </P>
      </>
    ),
  },
  {
    slug: "terceiro-visto-azul-whatsapp",
    title: "How does the third blue check on WhatsApp work?",
    description:
      "What the third blue check on WhatsApp is, how it works in chats and groups, advantages and privacy. Complete guide by Kentro.",
    keyword: "third blue check WhatsApp",
    category: "WhatsApp",
    author: "Kentro Team",
    date: "2026-04-10",
    readingMinutes: 4,
    related: ["atualizacao-audio-whatsapp"],
    internalLinks: [
      { label: "WhatsApp audio update", href: "/en/blog/atualizacao-audio-whatsapp" },
    ],
    faq: [
      { q: "Can the third blue check be disabled?", a: "Yes, in WhatsApp's account privacy settings." },
      { q: "Does it show in groups?", a: "The blue check in groups only appears when every participant reads the message." },
    ],
    content: (
      <>
        <P>
          The "third blue check" is how many people refer to WhatsApp's{" "}
          <Strong>read receipt</Strong> — when the second tick turns blue, indicating the message
          has been read. Understanding this signal matters for personal use and for businesses
          serving customers on the channel.
        </P>
        <H2 id="what-is">What is the third blue check?</H2>
        <P>
          Technically it is two blue ticks. The name "third check" popularized the idea of the
          final delivery step: sent, delivered and read. It is the most informative signal about
          recipient behavior.
        </P>
        <H2 id="how">How it works (chats and groups)</H2>
        <P>
          In 1:1 chats, ticks turn blue when the recipient opens the conversation. In groups,
          WhatsApp only paints the ticks blue when <Strong>all</Strong> participants have read.
          You can see who read and when by long-pressing the message.
        </P>
        <H2 id="advantages">Advantages (transparency, privacy, communication)</H2>
        <UL>
          <li>Transparency about real delivery of the message.</li>
          <li>Controlled privacy: the feature can be disabled in settings.</li>
          <li>More objective communication — you know whether to repeat the notice.</li>
        </UL>
        <H2 id="other">Other WhatsApp updates (audio transcription)</H2>
        <P>
          WhatsApp keeps evolving: automatic audio transcription, visual marking of listened
          audios and several group improvements. Tracking them is part of the job for anyone
          serving customers through the channel — see the post on the{" "}
          <a className="text-primary-glow hover:underline" href="/en/blog/atualizacao-audio-whatsapp">
            WhatsApp audio update
          </a>
          .
        </P>
        <H2 id="conclusion">Conclusion</H2>
        <P>
          The "third blue check" is simple, but affects how people and businesses communicate.
          In a service operation, more important than the blue tick is having history, metrics
          and a prepared team — which an omnichannel platform delivers.
        </P>
      </>
    ),
  },
  {
    slug: "confirmacao-de-consulta-pelo-whatsapp",
    title: "How to use WhatsApp to confirm medical appointments",
    description:
      "Reduce no-shows and improve patient communication using WhatsApp to confirm, cancel and reschedule appointments — with Kentro automation and CRM.",
    keyword: "appointment confirmation via WhatsApp",
    category: "Healthcare",
    author: "Kentro Team",
    date: "2026-03-28",
    readingMinutes: 7,
    related: ["papel-das-redes-sociais-no-atendimento", "cliente-no-centro-da-empresa"],
    internalLinks: [
      { label: "Clinic solutions", href: "/en/segmentos/clinicas" },
      { label: "Service management", href: "/en/gestao-de-atendimento" },
    ],
    faq: [
      { q: "Do WhatsApp reminders reduce no-shows?", a: "Yes, significantly: clinics that automate confirmation via WhatsApp typically reduce no-show by 20% to 40%." },
      { q: "Is it safe and LGPD-compliant?", a: "Yes, as long as privacy best practices are followed: consent, clear purpose, minimal necessary data and a secure channel." },
    ],
    content: (
      <>
        <P>
          No-shows are one of the biggest financial leaks for clinics and practices. The good
          news: most of them can be avoided with well-designed, automated{" "}
          <Strong>appointment confirmation via WhatsApp</Strong>.
        </P>
        <H2 id="importance">The importance of WhatsApp in appointment confirmation</H2>
        <UL>
          <li>Direct reduction in no-shows and delays.</li>
          <li>Communication on the channel the patient already uses every day.</li>
          <li>Engagement: the patient feels the clinic cares about their schedule.</li>
        </UL>
        <H2 id="implement">How to implement</H2>
        <H3>Sending reminders</H3>
        <P>
          The ideal is to send two reminders: one 48h before the appointment and another 2-3h
          before. Both should offer clear actions: confirm, reschedule or cancel.
        </P>
        <H3>Cancellation and rescheduling</H3>
        <P>
          The patient responds with one click or keyword; the system updates the schedule and
          frees the slot for another patient. Without this, the reminder is just a notification.
        </P>
        <H2 id="integration">Integrating WhatsApp with Kentro</H2>
        <P>
          Kentro connects WhatsApp Official to the clinic's schedule and the{" "}
          <a className="text-primary-glow hover:underline" href="/en/gestao-de-atendimento">
            service CRM
          </a>
          . Meta-approved templates, AI flows and time-based triggers deliver confirmation
          without manual work for reception.
        </P>
        <H2 id="best">Best practices (privacy, clear communication, timing)</H2>
        <UL>
          <li><Strong>LGPD:</Strong> collect consent and inform purpose.</li>
          <li><Strong>Data minimization:</Strong> do not send diagnoses or exams in the reminder.</li>
          <li><Strong>Timing:</Strong> avoid late nights and unusual weekends.</li>
          <li><Strong>Clear tone:</Strong> identify the clinic, offer options, easy opt-out.</li>
        </UL>
        <H2 id="conclusion">Conclusion</H2>
        <P>
          WhatsApp reminders have moved from nice-to-have to standard in well-run clinics.
          Combined with integrated scheduling and CRM, they form a system that practically
          eliminates avoidable no-shows.
        </P>
      </>
    ),
  },
  {
    slug: "cliente-no-centro-da-empresa",
    title: "Putting the customer at the center of your company",
    description:
      "Customer centricity in practice: how to put the customer at the center of operations with Kentro's CRM, omnichannel and AI tools.",
    keyword: "customer at the center of the company",
    category: "Strategy",
    author: "Kentro Team",
    date: "2026-03-12",
    readingMinutes: 6,
    related: ["papel-das-redes-sociais-no-atendimento", "confirmacao-de-consulta-pelo-whatsapp"],
    internalLinks: [
      { label: "Omnichannel strategy", href: "/en/omnichannel" },
      { label: "Integrations", href: "/en/integracoes" },
    ],
    content: (
      <>
        <P>
          "Putting the customer at the center" has become jargon. In practice, it is daily
          decisions about process, technology and culture. When a company truly adopts this
          mindset, the impact shows up in revenue, retention and word-of-mouth.
        </P>
        <H2 id="what">What it means to be a customer-centric company</H2>
        <P>
          It means every decision — product, marketing, service, billing — passes through the
          question: "does this improve or worsen the customer's experience?". It is not about
          giving up margin; it is about ending unnecessary friction.
        </P>
        <H2 id="tools">The role of Kentro's tools (CRM, omnichannel, AI)</H2>
        <UL>
          <li><Strong>CRM:</Strong> unified history, never ask what the customer already answered.</li>
          <li><Strong>Omnichannel:</Strong> the customer picks the channel — you track all of them.</li>
          <li><Strong>AI:</Strong> quick replies, context retrieved, team focused on what matters.</li>
        </UL>
        <H2 id="benefits">Practical benefits</H2>
        <UL>
          <li>Less operational rework.</li>
          <li>Higher NPS and referrals.</li>
          <li>More conversion in sales because customers notice the care.</li>
          <li>Decisions with data, not guesses.</li>
        </UL>
        <H2 id="conclusion">Conclusion</H2>
        <P>
          Customer centricity is the sum of a thousand small details — and none scales without
          technology. With well-implemented CRM, omnichannel and AI, putting the customer at the
          center moves from slogan to operation.
        </P>
      </>
    ),
  },
];

// Spanish translations
const POSTS_ES: BlogPost[] = [
  {
    slug: "papel-das-redes-sociais-no-atendimento",
    title: "El papel de las redes sociales en la atención al cliente",
    description:
      "Cómo usar las redes sociales en la atención al cliente: respuesta en tiempo real, transparencia y cómo el CRM omnicanal de Kentro lo centraliza todo.",
    keyword: "atención al cliente en redes sociales",
    category: "Atención al Cliente",
    author: "Equipo Kentro",
    date: "2026-05-14",
    readingMinutes: 6,
    related: ["cliente-no-centro-da-empresa", "confirmacao-de-consulta-pelo-whatsapp"],
    internalLinks: [
      { label: "Estrategia omnicanal", href: "/es/omnichannel" },
      { label: "Gestión de atención", href: "/es/gestao-de-atendimento" },
    ],
    content: (
      <>
        <P>
          Las redes sociales dejaron de ser solo un escaparate de marca: hoy son el primer canal
          donde la mayoría busca resolver dudas, reclamar o comprar. Atender bien en redes sociales
          significa atender al cliente en su terreno preferido.
        </P>
        <H2 id="por-que">Por qué las redes sociales son esenciales en la atención</H2>
        <P>
          La <Strong>atención al cliente en redes sociales</Strong> tiene tres rasgos que cambian
          las reglas: es pública, instantánea e informal. Estos tres pilares presionan a la empresa
          a ser más ágil — y más transparente.
        </P>
        <H2 id="tiempo-real">Atención en tiempo real</H2>
        <P>
          El cliente que pregunta por Instagram o WhatsApp espera respuesta en minutos, no en
          horas. Equipos que no logran ese ritmo pierden ventas y quedan expuestos a comentarios
          negativos. Automatizar la primera respuesta con IA y enrutamiento inteligente acorta el
          tiempo de espera sin perder personalización.
        </P>
        <H2 id="transparencia">Transparencia: la atención que todos ven</H2>
        <P>
          Cada interacción pública se vuelve referencia. Un comentario bien respondido convierte a
          otros prospectos. Un caso mal resuelto se viraliza en capturas. Por eso el tono y la
          postura deben estar estandarizados en todos los canales.
        </P>
        <H2 id="visao">Visión unificada con plataforma omnicanal</H2>
        <P>
          Cuando WhatsApp, Instagram, Facebook, email y voz viven en sistemas separados, el cliente
          repite la historia en cada canal. Una plataforma{" "}
          <a className="text-primary-glow hover:underline" href="/es/omnichannel">omnicanal</a>{" "}
          junta todo en un mismo historial con identidad única del cliente.
        </P>
        <H2 id="crm">CRM y chatbot para escalar la atención</H2>
        <P>
          El CRM de Kentro mantiene el historial completo de cada conversación, aplica etiquetas y
          oportunidades y dispara flujos automáticos. El chatbot con IA resuelve las dudas
          repetitivas y libera al equipo humano para lo que realmente requiere cuidado.
        </P>
        <H2 id="defensores">Cómo convertir clientes en defensores de la marca</H2>
        <UL>
          <li>Responde rápido — aunque sea para decir "ya lo estoy mirando".</li>
          <li>Sé humano: firma con el nombre del agente, evita respuestas robóticas.</li>
          <li>Resuelve en público, concluye en privado.</li>
          <li>Usa los datos para personalizar — llama por el nombre y cita el pedido.</li>
        </UL>
        <H2 id="conclusion">Conclusión</H2>
        <P>
          Atender bien en redes sociales es menos sobre herramientas y más sobre procesos — pero
          la herramienta correcta multiplica la capacidad del equipo. Centralizar canales,
          automatizar lo obvio y personalizar lo importante es como las marcas brasileñas están
          transformando la atención en ventaja competitiva.
        </P>
      </>
    ),
  },
  {
    slug: "atualizacao-audio-whatsapp",
    title: "Novedades de WhatsApp: todo sobre la actualización de audio",
    description:
      "Entiende la actualización de audio de WhatsApp (cambio de color de los audios escuchados) y cómo las herramientas omnicanal ayudan a las empresas a mantenerse al día.",
    keyword: "actualización de audio de WhatsApp",
    category: "WhatsApp",
    author: "Equipo Kentro",
    date: "2026-04-22",
    readingMinutes: 5,
    related: ["terceiro-visto-azul-whatsapp", "confirmacao-de-consulta-pelo-whatsapp"],
    internalLinks: [{ label: "Gestión de atención", href: "/es/gestao-de-atendimento" }],
    faq: [
      { q: "¿Qué cambió en el audio de WhatsApp?", a: "La indicación visual de audios ya escuchados: la onda de los audios oídos aparece en un color diferente al de los no oídos." },
      { q: "¿Afecta a las empresas?", a: "Sí. La atención por WhatsApp exige acompañar cambios de UX para orientar equipos y ajustar procesos." },
    ],
    content: (
      <>
        <P>
          WhatsApp lanzó una actualización aparentemente pequeña, pero con impacto directo en la
          rutina de atención: <Strong>los audios ya escuchados aparecen ahora con un color
          diferente</Strong> a los que aún no fueron oídos. Para quien trabaja con alto volumen de
          mensajes, este detalle cambia el flujo de trabajo.
        </P>
        <H2 id="cambio">Entendiendo la actualización de audio de WhatsApp</H2>
        <P>
          El cambio es visual: la forma de onda del audio aparece en un tono más claro cuando ya
          lo escuchaste, ayudando a identificar rápidamente lo que aún requiere atención. En
          conversaciones largas, evita que mensajes importantes pasen por alto.
        </P>
        <H2 id="omnicanal">La importancia de las herramientas omnicanal</H2>
        <P>
          En una operación profesional, escuchar cada audio dentro de la app puede no ser viable.
          En una plataforma omnicanal como Kentro, los audios entran en la misma fila de atención,
          con transcripción automática, etiquetas e indicadores de lectura — independientemente de
          lo que muestre la app de WhatsApp.
        </P>
        <H2 id="seguir">Por qué seguir las actualizaciones de WhatsApp</H2>
        <UL>
          <li>Nuevas funciones cambian la expectativa del cliente en pocas semanas.</li>
          <li>Los equipos deben ser entrenados para usar (o no) lo que la app ofrece.</li>
          <li>Quien usa API Oficial debe entender qué cambia en la app y qué en el protocolo.</li>
        </UL>
        <H2 id="conclusion">Conclusión</H2>
        <P>
          La actualización de audio es un recordatorio: la UX de WhatsApp evoluciona todo el
          tiempo. Quien combina la app con una plataforma de atención gana previsibilidad — y no
          queda rehén de cambios puntuales.
        </P>
      </>
    ),
  },
  {
    slug: "terceiro-visto-azul-whatsapp",
    title: "¿Cómo funciona el tercer visto azul de WhatsApp?",
    description:
      "Qué es el tercer visto azul de WhatsApp, cómo funciona en chats y grupos, ventajas y privacidad. Guía completa de Kentro.",
    keyword: "tercer visto azul WhatsApp",
    category: "WhatsApp",
    author: "Equipo Kentro",
    date: "2026-04-10",
    readingMinutes: 4,
    related: ["atualizacao-audio-whatsapp"],
    internalLinks: [
      { label: "Actualización de audio de WhatsApp", href: "/es/blog/atualizacao-audio-whatsapp" },
    ],
    faq: [
      { q: "¿Se puede desactivar el tercer visto azul?", a: "Sí, en la configuración de privacidad de la cuenta de WhatsApp." },
      { q: "¿Aparece en grupos?", a: "El visto azul en grupos solo aparece cuando todos los participantes leen el mensaje." },
    ],
    content: (
      <>
        <P>
          El "tercer visto azul" es como muchos llaman a la{" "}
          <Strong>confirmación de lectura</Strong> de WhatsApp — cuando el segundo tilde se vuelve
          azul, indicando que el mensaje fue leído. Entender esta señal importa tanto para uso
          personal como para empresas que atienden por el canal.
        </P>
        <H2 id="que-es">¿Qué es el tercer visto azul?</H2>
        <P>
          Técnicamente son dos tildes azules. El nombre "tercer visto" popularizó la idea del paso
          final de la entrega: enviado, entregado y leído. Es la señal más informativa sobre el
          comportamiento del destinatario.
        </P>
        <H2 id="como">Cómo funciona (chats y grupos)</H2>
        <P>
          En chats 1:1, los tildes se vuelven azules cuando el destinatario abre la conversación.
          En grupos, WhatsApp solo pinta los tildes de azul cuando <Strong>todos</Strong> los
          participantes leyeron. Puedes ver quién leyó y cuándo manteniendo presionado el mensaje.
        </P>
        <H2 id="ventajas">Ventajas (transparencia, privacidad, comunicación)</H2>
        <UL>
          <li>Transparencia sobre la entrega real del mensaje.</li>
          <li>Privacidad controlada: la función se puede desactivar en ajustes.</li>
          <li>Comunicación más objetiva — sabes si necesitas repetir el aviso.</li>
        </UL>
        <H2 id="otras">Otras actualizaciones de WhatsApp (transcripción de audio)</H2>
        <P>
          WhatsApp sigue evolucionando: transcripción automática de audios, marcación visual de
          audios ya escuchados y varias mejoras en grupos. Acompañar es parte del trabajo de quien
          atiende por el canal — ve el post sobre la{" "}
          <a className="text-primary-glow hover:underline" href="/es/blog/atualizacao-audio-whatsapp">
            actualización de audio de WhatsApp
          </a>
          .
        </P>
        <H2 id="conclusion">Conclusión</H2>
        <P>
          El "tercer visto azul" es simple, pero afecta cómo personas y empresas se comunican. En
          una operación de atención, más importante que el tilde azul es tener historial, métricas
          y equipo preparado — lo que una plataforma omnicanal garantiza.
        </P>
      </>
    ),
  },
  {
    slug: "confirmacao-de-consulta-pelo-whatsapp",
    title: "Cómo usar WhatsApp para confirmar citas médicas",
    description:
      "Reduce ausencias y mejora la comunicación con pacientes usando WhatsApp para confirmar, cancelar y reprogramar citas — con automatización y CRM de Kentro.",
    keyword: "confirmación de cita por WhatsApp",
    category: "Salud",
    author: "Equipo Kentro",
    date: "2026-03-28",
    readingMinutes: 7,
    related: ["papel-das-redes-sociais-no-atendimento", "cliente-no-centro-da-empresa"],
    internalLinks: [
      { label: "Soluciones para clínicas", href: "/es/segmentos/clinicas" },
      { label: "Gestión de atención", href: "/es/gestao-de-atendimento" },
    ],
    faq: [
      { q: "¿Los recordatorios por WhatsApp reducen las ausencias?", a: "Sí, de forma significativa: las clínicas que automatizan la confirmación por WhatsApp suelen reducir el no-show entre 20% y 40%." },
      { q: "¿Es seguro y cumple con la LGPD?", a: "Sí, siempre que se sigan buenas prácticas de privacidad: consentimiento, finalidad clara, mínimo de datos y canal seguro." },
    ],
    content: (
      <>
        <P>
          Las ausencias (no-show) son uno de los mayores desagües financieros de clínicas y
          consultorios. La buena noticia: la mayoría puede evitarse con{" "}
          <Strong>confirmación de cita por WhatsApp</Strong>, automatizada y bien diseñada.
        </P>
        <H2 id="importancia">La importancia de WhatsApp en la confirmación de citas</H2>
        <UL>
          <li>Reducción directa de ausencias y retrasos.</li>
          <li>Comunicación en el canal que el paciente ya usa todos los días.</li>
          <li>Compromiso: el paciente siente que la clínica se preocupa por su agenda.</li>
        </UL>
        <H2 id="implementar">Cómo implementarlo</H2>
        <H3>Envío de recordatorios</H3>
        <P>
          Lo ideal es enviar dos recordatorios: uno 48h antes de la cita y otro 2-3h antes. Ambos
          deben ofrecer acciones claras: confirmar, reprogramar o cancelar.
        </P>
        <H3>Cancelación y reprogramación</H3>
        <P>
          El paciente responde con un clic o palabra clave; el sistema actualiza la agenda y
          libera el horario para otro paciente. Sin esto, el recordatorio es solo una notificación.
        </P>
        <H2 id="integracion">Integrando WhatsApp con Kentro</H2>
        <P>
          Kentro conecta WhatsApp Oficial a la agenda de la clínica y al{" "}
          <a className="text-primary-glow hover:underline" href="/es/gestao-de-atendimento">
            CRM de atención
          </a>
          . Plantillas aprobadas por Meta, flujos con IA y disparadores por horario entregan la
          confirmación sin trabajo manual de recepción.
        </P>
        <H2 id="buenas">Buenas prácticas (privacidad, comunicación clara, horarios)</H2>
        <UL>
          <li><Strong>LGPD:</Strong> recopila consentimiento e informa la finalidad.</li>
          <li><Strong>Mínimo de datos:</Strong> no envíes diagnósticos ni exámenes en el recordatorio.</li>
          <li><Strong>Horarios:</Strong> evita madrugadas y fines de semana inesperados.</li>
          <li><Strong>Tono claro:</Strong> identifica la clínica, ofrece opciones, deja el opt-out fácil.</li>
        </UL>
        <H2 id="conclusion">Conclusión</H2>
        <P>
          El recordatorio por WhatsApp dejó de ser opcional para volverse estándar en clínicas
          organizadas. Combinado con agenda integrada y CRM, se convierte en un sistema que
          prácticamente elimina el no-show evitable.
        </P>
      </>
    ),
  },
  {
    slug: "cliente-no-centro-da-empresa",
    title: "Poniendo al cliente en el centro de tu empresa",
    description:
      "Customer centricity en la práctica: cómo poner al cliente en el centro de la operación con las herramientas de CRM, omnicanal e IA de Kentro.",
    keyword: "cliente en el centro de la empresa",
    category: "Estrategia",
    author: "Equipo Kentro",
    date: "2026-03-12",
    readingMinutes: 6,
    related: ["papel-das-redes-sociais-no-atendimento", "confirmacao-de-consulta-pelo-whatsapp"],
    internalLinks: [
      { label: "Estrategia omnicanal", href: "/es/omnichannel" },
      { label: "Integraciones", href: "/es/integracoes" },
    ],
    content: (
      <>
        <P>
          "Poner al cliente en el centro" se volvió un cliché. En la práctica, son decisiones
          diarias sobre proceso, tecnología y cultura. Cuando una empresa adopta esa mentalidad de
          verdad, el impacto aparece en ingresos, retención y recomendación espontánea.
        </P>
        <H2 id="que-significa">Qué significa ser una empresa centrada en el cliente</H2>
        <P>
          Significa que cada decisión — de producto, marketing, atención, cobro — pasa por la
          pregunta: "¿esto mejora o empeora la experiencia del cliente?". No es renunciar al
          margen; es dejar de crear fricción innecesaria.
        </P>
        <H2 id="herramientas">El papel de las herramientas de Kentro (CRM, omnicanal, IA)</H2>
        <UL>
          <li><Strong>CRM:</Strong> historial unificado, sin preguntar lo que el cliente ya respondió.</li>
          <li><Strong>Omnicanal:</Strong> el cliente elige el canal — tú lo sigues en todos.</li>
          <li><Strong>IA:</Strong> respuestas rápidas, contexto recuperado, equipo enfocado en lo importante.</li>
        </UL>
        <H2 id="beneficios">Beneficios en la práctica</H2>
        <UL>
          <li>Menos retrabajo operativo.</li>
          <li>Aumento de NPS y de recomendaciones.</li>
          <li>Más conversión en ventas, porque el cliente percibe el cuidado.</li>
          <li>Decisiones con datos, no con suposiciones.</li>
        </UL>
        <H2 id="conclusion">Conclusión</H2>
        <P>
          Customer centricity es la suma de mil pequeños detalles — y ninguno escala sin
          tecnología. Con CRM, omnicanal e IA bien implementados, poner al cliente en el centro
          deja de ser eslogan y se vuelve operación.
        </P>
      </>
    ),
  },
];

export const POSTS_BY_LOCALE: Record<Locale, BlogPost[]> = {
  en: POSTS_EN,
  es: POSTS_ES,
};

export function getPosts(locale: Locale): BlogPost[] {
  return POSTS_BY_LOCALE[locale];
}

export function getPostBySlug(locale: Locale, slug: string): BlogPost | undefined {
  return POSTS_BY_LOCALE[locale].find((p) => p.slug === slug);
}

// UI strings (blog index / post chrome)
export const BLOG_UI = {
  en: {
    heroTitle: "Kentro News",
    heroSubtitle: "Content on AI, omnichannel service, sales and growth.",
    featured: "Featured articles",
    more: "More content",
    moreSub: "Posts published by our ecosystem.",
    breadcrumbHome: "Home",
    breadcrumbBlog: "Blog",
    minutes: "min read",
    by: "By",
    faqHeading: "Frequently asked questions",
    continueReading: "Continue reading",
    related: "Related posts",
    backToBlog: "Back to the blog",
    ctaTitle: "Ready to evolve your customer service?",
    ctaDesc: "Talk to a Kentro specialist and see the platform running with your own flows.",
    ctaPrimary: "Request a demo",
    ctaWhatsAppMsg: "Hello! I came from the Kentro blog and would like to talk to a specialist.",
    notFoundTitle: "Post not found",
    notFoundDesc: "The content you are looking for does not exist.",
    dateLocale: "en-US",
  },
  es: {
    heroTitle: "Kentro News",
    heroSubtitle: "Contenido sobre IA, atención omnicanal, ventas y crecimiento.",
    featured: "Artículos destacados",
    more: "Más contenido",
    moreSub: "Posts publicados por nuestro ecosistema.",
    breadcrumbHome: "Inicio",
    breadcrumbBlog: "Blog",
    minutes: "min de lectura",
    by: "Por",
    faqHeading: "Preguntas frecuentes",
    continueReading: "Sigue leyendo",
    related: "Posts relacionados",
    backToBlog: "Volver al blog",
    ctaTitle: "¿Listo para evolucionar tu atención?",
    ctaDesc: "Habla con un especialista de Kentro y ve la plataforma con tus propios flujos.",
    ctaPrimary: "Solicitar demostración",
    ctaWhatsAppMsg: "¡Hola! Vine del blog de Kentro y quiero hablar con un especialista.",
    notFoundTitle: "Post no encontrado",
    notFoundDesc: "El contenido que buscas no existe.",
    dateLocale: "es-ES",
  },
} as const;
